// 2026-10-17 易用性 U3 — 全局快捷键 hook
// 设计原则:
//   1. input/textarea/contenteditable focus 时不抢键(避免在表单里按 s 触发保存)
//   2. 修饰键 (Cmd/Ctrl/Alt) 触发的快捷键单独分类,不影响单键快捷键
//   3. 两段组合(g s / g d / g w)用 chord 模式:第一段在 1s 内按第二段才算
//   4. 支持 antd Modal/Input/Select 等所有原生 input/textarea 焦点
//   5. cleanup 必做(useEffect return 卸载监听器)

import { useEffect, useRef } from 'react'

const CHORD_TIMEOUT_MS = 1000

/**
 * 检查事件是否在可编辑元素中(input/textarea/contenteditable)
 * @param {KeyboardEvent} e
 * @returns {boolean}
 */
function isEditableTarget(e) {
  const t = e.target
  if (!t) return false
  const tag = t.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (t.isContentEditable) return true
  return false
}

/**
 * 检查修饰键状态
 * @param {KeyboardEvent} e
 * @param {string|null} require - 需要的修饰键组合,如 'ctrl' / 'cmd' / 'shift' / 'alt' / null
 * @returns {boolean}
 */
function matchModifier(e, require) {
  const hasCtrl = e.ctrlKey
  const hasMeta = e.metaKey
  const hasShift = e.shiftKey
  const hasAlt = e.altKey

  // require=null 时,不允许任意修饰键(纯单键)
  if (require == null) {
    return !hasCtrl && !hasMeta && !hasShift && !hasAlt
  }

  const parts = require.toLowerCase().split('+')
  const wantCtrl = parts.includes('ctrl')
  const wantCmd = parts.includes('cmd') || parts.includes('meta')
  const wantShift = parts.includes('shift')
  const wantAlt = parts.includes('alt')

  return (
    (wantCtrl ? hasCtrl : !hasCtrl) &&
    (wantCmd ? hasMeta : !hasMeta) &&
    (wantShift ? hasShift : !hasShift) &&
    (wantAlt ? hasAlt : !hasAlt)
  )
}

/**
 * 注册全局键盘快捷键
 *
 * @param {Array<{key: string, require?: string|null, handler: (e: KeyboardEvent) => void, allowInEditable?: boolean, description?: string, category?: string}>} shortcuts
 *   - key: 单字符 'k' / 'g' / 's' 等;特殊键 'Escape' 'Enter' 'ArrowUp' 等
 *   - require: 修饰键 'ctrl' / 'cmd' / 'shift' / 'alt' / 组合如 'cmd+shift' / null(默认单键)
 *   - handler: 触发函数,函数会拿到原始 KeyboardEvent
 *   - allowInEditable: 默认 false,在 input/textarea focus 时不触发;true 表示例外(如 Esc 关 modal)
 *   - description: 描述文本,给 ShortcutsModal 用
 *   - category: 分类名,给 ShortcutsModal 分组
 *
 * @example
 *   useShortcuts([
 *     { key: 'k', require: 'cmd', handler: () => setSearchOpen(true), description: '打开全局搜索', category: '导航' },
 *     { key: '?', handler: () => setHelpOpen(true), description: '显示快捷键', category: '帮助' },
 *   ])
 *
 * 组合键(两段按) — 用一个 'g' 单键 handler 自己管状态即可,见 App.jsx
 */
export default function useShortcuts(shortcuts) {
  // 用 ref 镜像 shortcuts,避免 handler 每次重建导致 useEffect 重挂监听器
  const shortcutsRef = useRef(shortcuts)
  shortcutsRef.current = shortcuts

  useEffect(() => {
    const handler = (e) => {
      const inEditable = isEditableTarget(e)
      for (const sc of shortcutsRef.current) {
        if (!sc) continue
        if (inEditable && !sc.allowInEditable) continue
        if (e.key !== sc.key) continue
        if (!matchModifier(e, sc.require ?? null)) continue
        sc.handler(e)
        return
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])
}

/**
 * 工具:从 KeyboardEvent 取可见快捷键字符串(给 ShortcutsModal 用)
 * @param {string} key
 * @param {string|null} require
 * @returns {string}
 */
export function formatShortcut(key, require) {
  // 平台差异:Mac 显示 ⌘ / ⇧ / ⌥ / ⌃;其他显示 Ctrl / Shift / Alt
  const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform)
  const parts = []
  if (require) {
    const tokens = require.toLowerCase().split('+')
    if (tokens.includes('ctrl')) parts.push(isMac ? '⌃' : 'Ctrl')
    if (tokens.includes('cmd') || tokens.includes('meta')) parts.push(isMac ? '⌘' : 'Ctrl')
    if (tokens.includes('shift')) parts.push(isMac ? '⇧' : 'Shift')
    if (tokens.includes('alt')) parts.push(isMac ? '⌥' : 'Alt')
  }
  const k = key === '?' ? '?' : key
  parts.push(k)
  return parts.join(' + ')
}

export { CHORD_TIMEOUT_MS }
